import { Component, input, inject } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { TableRow } from "@bitwarden/web-vault/app/components/tables/transaction-table/transaction-table";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { IReferenceData } from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import { isCurrency } from "@bitwarden/web-vault/app/models/view/symbol/currency-utility";
import { RIGHT_PANEL_CONFIG } from "@bitwarden/web-vault/app/models/constants/global.constants";

const EXCHGE_RATE = 2;

export interface TransactionData {
  exchangeHistory: Array<IReferenceData>;
  balance: number;
  date: string;
  symbol: string;
  transactionSymbol: string;
}

export function getData(
  rowData: TableRow,
  exchangeHistory: Array<IReferenceData>,
): TransactionData {
  return {
    balance: rowData?.transactionView?.c_valuation?.value?.amount,
    date: rowData.transactionDate,
    symbol: rowData.symbol,
    transactionSymbol: rowData.transactionView?.quantity?.actualQuantity?.symbol,
    exchangeHistory,
  };
}

@Component({
  templateUrl: "./revaluation-transaction.component.html",
})
export class RevaluationTransactionComponent {
  rowData = input<TableRow>();
  transactionStoreService = inject(TransactionStoreService);
  userStoreService = inject(UserStoreService);

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<RevaluationTransactionComponent>,
  ) {}

  get data(): TransactionData {
    const rowData = this.rowData();
    const exchangeHistory = this.transactionStoreService.generatedSymbols
      .collection()
      .find((sym) => sym.code === rowData.transactionView?.quantity.actualQuantity.symbol)?.history;
    return getData(rowData, exchangeHistory ?? []);
  }

  get exchangeRate(): Array<IReferenceData> {
    const result: IReferenceData[] = [];
    const targetDate = new Date(this.data.date);
    const exchangeHistory = this.data.exchangeHistory;

    if (!exchangeHistory) {
      return result;
    }

    for (let i = 0; i < this.data.exchangeHistory.length; i++) {
      const entry = this.data.exchangeHistory[i];

      if (new Date(entry.date.date) > targetDate) {
        continue;
      }
      if (result.length === 0) {
        result.push(entry);
      } else if (entry.rate !== result[0].rate) {
        result.push(entry);
        break;
      }
    }

    return result;
  }

  get exchRateDiff(): number {
    const history = this.data.exchangeHistory;

    if (history.length >= EXCHGE_RATE) {
      return history[0].rate - history[1].rate;
    }
    return 0;
  }

  get coreCurrency(): string {
    return this.userStoreService.preferences.preferenceView().coreCurrency;
  }

  get isSymbol(): boolean {
    return isCurrency(this.data.transactionSymbol);
  }

  static open(
    rowData: TableRow,
    dialog: MatDialog,
    dialogRef?: MatDialogRef<DialogWrapperComponent>,
  ): void {
    dialogRef?.close();
    dialog.open(DialogWrapperComponent, {
      ...RIGHT_PANEL_CONFIG,
      disableClose: false,
      data: {
        title: "revalTransaction",
        showBackButton: false,
        component: RevaluationTransactionComponent,
        componentInputs: { rowData },
      },
    });
  }
}
